import { z } from 'zod/v4';
import { CreatedQuestionnaireSchema } from './questionnaire-create.js';

export const GetQuestionnaireBuilderParamsSchema = z.object({
  id: z.string().min(1),
});

export type GetQuestionnaireBuilderParams = z.infer<
  typeof GetQuestionnaireBuilderParamsSchema
>;

/**
 * Answer statistics for the questionnaire.
 *
 * A **closed** object on purpose: the legacy response schema declared
 * `additionalProperties: false` here, so the serialiser truncated the stored
 * blob to exactly these keys. Zod strips undeclared keys, reproducing that.
 *
 * Every key is **nullable**, and the percentages carry no range constraint.
 * Both matter, because this schema serialises a response rather than
 * validating an input:
 *
 * - The builder PUT recomputes the blob and writes `null` into the four
 *   `total*Requests` counts for a questionnaire nobody has been invited to.
 *   fast-json-stringify coerced those to `0`; Zod rejects them outright and
 *   answers 500, which is what broke every builder screen in CI.
 * - The legacy JSON Schema's `minimum`/`maximum` were never enforced —
 *   fast-json-stringify only coerces types on the way out. Porting them into
 *   Zod would turn documentation into a 500 the first time a stored
 *   percentage lands outside 0–100.
 *
 * Both frontends already coalesce every one of these (`?? 0` in v2's tab-badge
 * sum, `|| 0` in the old app's progress tiles), so `null` reaches them safely.
 */
const statistic = () => z.number().nullable().optional();

export const QuestionnaireBuilderStatisticsSchema = z.object({
  totalApprovedResponses: statistic(),
  totalInReviewResponses: statistic(),
  totalDeniedResponses: statistic(),
  totalMissingResponses: statistic(),
  totalQuestions: statistic(),
  approvedResponsesPercentage: statistic(),
  inReviewResponsesPercentage: statistic(),
  deniedResponsesPercentage: statistic(),
  missingResponsesPercentage: statistic(),
  totalOpenRequests: statistic(),
  totalInReviewRequests: statistic(),
  totalCompletedRequests: statistic(),
  totalCompaniesInvited: statistic(),
  totalRequestChanges: statistic(),
});

export type QuestionnaireBuilderStatistics = z.infer<
  typeof QuestionnaireBuilderStatisticsSchema
>;

/**
 * One section of the builder.
 *
 * **Closed**, unlike almost every other row schema in this module: the legacy
 * section item schema declared `additionalProperties: false`, so the fifteen
 * audit columns the SQL returned never reached the wire. Keeping it closed
 * preserves that — the new SELECT simply does not read them.
 *
 * `sortOrder` is a number here where the legacy schema declared `string` and so
 * stringified it. Neither frontend reads the field, and both type it
 * `number | string`, so the column's real type is surfaced instead.
 */
export const QuestionnaireBuilderSectionSchema = z.object({
  id: z.string(),
  name: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  isRetraced: z.string().nullable().optional(),
  sortOrder: z.number().nullable().optional(),
});

export type QuestionnaireBuilderSection = z.infer<
  typeof QuestionnaireBuilderSectionSchema
>;

/**
 * One question of the builder.
 *
 * `looseObject` because the legacy question item schema was
 * `additionalProperties: true` **and** the legacy transformer spread the parsed
 * `meta` blob flat onto the question. Both frontends depend on that flattening:
 * frontend-v2's `normalizeQuestion` reads `question.options` /
 * `question.isCompulsory` / `question.attachmentFileName` alongside their
 * `meta.*` twins, and the old app's `transformQuestionForBuilder` reads only the
 * flattened names.
 *
 * `type` and `sectionId` are synthesized aliases of `questionTypeId` and
 * `questionnaireSectionId`, as in the legacy transformer.
 *
 * `meta` is always an object — never `null`. The legacy `isValidJson` guard
 * returned `true` for a `null` column, so `JSON.parse(null)` produced `meta:
 * null` for questions with no blob; normalising to `{}` is what the guard
 * intended and is what both normalizers expect.
 */
export const QuestionnaireBuilderQuestionSchema = z.looseObject({
  id: z.string(),
  title: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  companyId: z.string().nullable().optional(),
  sortOrder: z.number().nullable().optional(),
  questionTypeId: z.string().nullable().optional(),
  questionnaireSectionId: z.string().nullable().optional(),
  retracedDatapointId: z.string().nullable().optional(),
  /** Alias of `questionTypeId`. */
  type: z.string().nullable().optional(),
  /** Alias of `questionnaireSectionId`. */
  sectionId: z.string().nullable().optional(),
  /** `'YES'` in the column, a boolean on the wire. */
  isCustom: z.boolean(),
  meta: z.looseObject({}),
});

export type QuestionnaireBuilderQuestion = z.infer<
  typeof QuestionnaireBuilderQuestionSchema
>;

/**
 * `GET /api/questionnaires/:id/builder` — the questionnaire row plus its
 * sections and questions, the single read behind the builder, preview and
 * summary screens.
 *
 * Built on `CreatedQuestionnaireSchema` because the questionnaire row comes from
 * the same `getQuestionnaireByIdDb` projection, with two overrides:
 *
 * - `isTemplate` is a **boolean**, not the `0`/`1` the column stores. The legacy
 *   response schema declared `type: 'boolean'` so the serialiser coerced it, and
 *   frontend-v2 types `RawBuilderDetail.isTemplate` as a boolean.
 * - `statistics` is the closed 14-key object above rather than a loose blob.
 *
 * Everything else stays loose: the legacy top-level schema was
 * `additionalProperties: true`, and frontend-v2 reads audit columns
 * (`updatedByUserName`, `updatedByCompanyName`, `createdAt`, `updatedAt`) that
 * ride along on the row.
 */
export const QuestionnaireBuilderDetailSchema =
  CreatedQuestionnaireSchema.extend({
    isTemplate: z.boolean().nullable().optional(),
    statistics: QuestionnaireBuilderStatisticsSchema.nullable().optional(),
    sections: z.array(QuestionnaireBuilderSectionSchema),
    questions: z.array(QuestionnaireBuilderQuestionSchema),
  });

export type QuestionnaireBuilderDetail = z.infer<
  typeof QuestionnaireBuilderDetailSchema
>;
